import { createContext, useContext, useState, useEffect, type ReactNode } from 'react';
import { signInWithPhoneNumber, RecaptchaVerifier, type ConfirmationResult } from 'firebase/auth';
import { auth as firebaseAuth } from '../firebase';

interface User {
  id: string;
  role: string;
  status: string;
  email?: string;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  csrfToken: string | null;
  login: (phone: string) => Promise<ConfirmationResult>;
  verifyCode: (confirmationResult: ConfirmationResult, code: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  useEffect(() => {
    // Try to restore session on mount
    fetch('/api/v1/profiles/me', { credentials: 'include' })
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data?.success && data.data?.user) {
          setUser(data.data.user);
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const login = async (phone: string) => {
    const recaptchaVerifier = new RecaptchaVerifier(firebaseAuth, 'recaptcha-container', {
      size: 'invisible',
    });
    return signInWithPhoneNumber(firebaseAuth, phone, recaptchaVerifier);
  };

  const verifyCode = async (confirmationResult: ConfirmationResult, code: string) => {
    const result = await confirmationResult.confirm(code);
    const idToken = await result.user.getIdToken();

    const response = await fetch('/api/v1/auth/firebase', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ idToken }),
    });

    const data = await response.json();
    if (data.success) {
      setUser(data.data.user);
      setCsrfToken(data.data.csrfToken);
    } else {
      throw new Error(data.error || 'Authentication failed');
    }
  };

  const logout = async () => {
    await fetch('/api/v1/auth/logout', {
      method: 'POST',
      credentials: 'include',
      headers: csrfToken ? { 'X-CSRF-Token': csrfToken } : {},
    });
    await firebaseAuth.signOut();
    setUser(null);
    setCsrfToken(null);
  };

  return (
    <AuthContext.Provider value={{ user, loading, csrfToken, login, verifyCode, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
